import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'fr' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  fr: {
    // Header
    'home': 'Accueil',
    'welcome': 'Bienvenue',
    'services': 'Services',
    'about': 'À propos',
    'templates': 'Modèles',
    'testimonials': 'Témoignages',
    'blog': 'Blog',
    'contact': 'Contact',
    'start': 'Commencer',
    
    // Hero
    'expert.title': 'Expert en rédaction de CV professionnels',
    'hero.title': 'Transformez votre carrière avec un CV gagnant',
    'hero.description': 'Ne laissez pas votre CV vous priver de l\'emploi de vos rêves. Dans un marché de l\'emploi compétitif, chaque détail compte. Avec la montée des candidatures et des processus automatisés (ATS), un CV mal optimisé peut passer inaperçu. Mettez toutes les chances de votre côté!',
    'hero.cta': 'Démarrer mon CV',
    'hero.secondary': 'En savoir plus',
    'hero.clients': 'Plus de 800 clients satisfaits',
    
    // Welcome
    'welcome.title': 'BIENVENUE',
    'welcome.subtitle': 'Ne laissez pas votre CV vous priver de l\'emploi de vos rêves.',
    'welcome.paragraph1': 'Dans un marché de l\'emploi compétitif, chaque détail compte. Avec la montée des candidatures et des processus automatisés (ATS), un CV mal optimisé peut passer inaperçu. Mettez toutes les chances de votre côté!',
    'welcome.paragraph2': 'Confiez la rédaction et l\'optimisation de votre CV à CV Pro Québec et décrochez plus d\'entretiens grâce à un document professionnel, percutant et compatible avec les recruteurs et les systèmes de sélection automatisés ATS.',
    'welcome.services': 'Nos services',
    'welcome.start': 'Commencer maintenant',
    
    // Services
    'services.title': 'Nos services de rédaction professionnelle',
    'services.subtitle': 'Pourquoi choisir CV Pro Québec?',
    'services.intro': 'Dans un marché du travail ultra-compétitif, votre CV est bien plus qu\'un simple document — c\'est votre ticket d\'entrée pour décrocher l\'entrevue de vos rêves. Malheureusement, même le plus beau des CV peut vous fermer des portes s\'il n\'est pas optimisé stratégiquement.',
    'services.traps.title': 'Les pièges des solutions standards',
    'services.traps.description': 'De nombreux sites proposent des gabarits attrayants ou des outils d\'auto-rédaction, mais ces solutions présentent des lacunes critiques:',
    'services.traps.item1': 'Incompatibilité avec les systèmes ATS — 75 % des CV sont automatiquement rejetés avant même d\'être lus par un recruteur',
    'services.traps.item2': 'Contenu générique non adapté au poste visé — absence des mots-clés stratégiques recherchés par les recruteurs',
    'services.traps.item3': 'Compétences pertinentes omises — vos expériences passées recèlent des atouts que vous n\'avez pas mis en valeur',
    'services.traps.item4': 'Problèmes linguistiques — fautes d\'orthographe, de grammaire ou de syntaxe qui nuisent à votre professionnalisme',
    'services.traps.item5': 'Manque de cohérence narrative — absence d\'un fil conducteur qui valorise votre parcours unique',
    
    // New Services Content
    'services.approach.title': 'Notre approche: La personnalisation stratégique',
    'services.approach.intro': 'Chez CV Pro Québec, nous ne vous offrons pas un simple document, mais une véritable stratégie de positionnement professionnel :',
    
    'services.analysis.title': '1. Analyse approfondie de votre profil',
    'services.analysis.intro': 'Notre processus commence par une collecte d\'informations complète :',
    'services.analysis.item1': 'Profil LinkedIn : votre expérience professionnelle et vos formations',
    'services.analysis.item2': 'Analyse de vos CV précédents pour identifier les compétences inexploitées',
    'services.analysis.item3': 'Questionnaire personnalisé pour découvrir vos réalisations oubliées',
    'services.analysis.item4': 'Conversation guidée pour comprendre votre vision professionnelle',
    
    'services.optimization.title': '2. Optimisation ciblée pour le poste convoité',
    'services.optimization.intro': 'Nous créons un CV sur mesure qui :',
    'services.optimization.item1': 'Intègre stratégiquement les mots-clés de la description du poste visé',
    'services.optimization.item2': 'Met en valeur uniquement les expériences et compétences pertinentes',
    'services.optimization.item3': 'Quantifie vos réalisations avec des données concrètes',
    'services.optimization.item4': 'Respecte scrupuleusement les normes de l\'Office québécois de la langue française',
    'services.optimization.item5': 'Passe les filtres ATS avec un score optimal',
    
    'services.support.title': '3. Accompagnement complet pour maximiser vos chances',
    'services.support.intro': 'Au-delà du CV, nous vous offrons :',
    'services.support.item1': 'Rédaction d\'une lettre de présentation percutante qui renforce votre candidature',
    'services.support.item2': 'Conseils pratiques pour aligner votre profil LinkedIn avec votre nouveau CV',
    'services.support.item3': 'Recommandations personnalisées pour vous préparer aux questions d\'entrevue probables',
    'services.support.item4': 'Suivi post-livraison pour répondre à vos questions',
    
    'services.guarantee.title': 'Notre garantie d\'excellence',
    'services.guarantee.intro': 'Contrairement aux services génériques, nous nous engageons à créer un document qui :',
    'services.guarantee.item1': 'Représente fidèlement votre parcours professionnel',
    'services.guarantee.item2': 'Met en lumière vos compétences les plus pertinentes pour le poste visé',
    'services.guarantee.item3': 'Respecte les normes linguistiques et de présentation du marché québécois',
    'services.guarantee.item4': 'Est optimisé pour traverser les filtres ATS avec succès',
    'services.guarantee.item5': 'Vous positionne comme le candidat idéal aux yeux des recruteurs',
    
    'services.howto.title': 'Comment ça marche',
    'services.howto.step1': 'Étape 1 : Choisissez votre forfait et passez commande.',
    'services.howto.step2': 'Étape 2 : Soumettez vos informations (CV actuel, LinkedIn, description de poste, etc.).',
    'services.howto.step3': 'Étape 3 : Recevez votre CV personnalisé sous 24 à 48 heures!',
    'services.howto.note': 'Pourquoi ? Ça clarifie le processus sans entrer dans les détails techniques, et ça rassure sur la facilité d\'utilisation.',
    
    'services.conclusion': 'Un CV mal optimisé peut vous coûter des mois de recherche d\'emploi et des milliers de dollars en opportunités manquées. Ne laissez pas un document médiocre se dresser entre vous et le poste de vos rêves.',
    'services.tagline': 'CV Pro Québec — Parce que votre carrière mérite un document à la hauteur de votre talent.',

    // About
    'about.title': 'Bonjour!',
    'about.subtitle': 'Je suis Gérald Doré, le cœur derrière CV Pro Québec.',
    'about.section1.title': 'Mon parcours',
    'about.section1.description': 'Au fil des ans, j\'ai vu trop de talents se perdre dans l\'ombre d\'un CV mal présenté. Des ingénieurs brillants, des artisans passionnés, des étudiants prometteurs – tous qualifiés, mais freinés par des détails : fautes d\'orthographe, verbes d\'action mal choisis, ou une mise en page chaotique.',

    // Testimonials
    'testimonials.title': 'Ce que nos clients disent',
    'testimonials.subtitle': 'Découvrez comment nos services ont aidé des professionnels à faire avancer leur carrière',
    'testimonials.banner.title': 'Prêt à transformer votre carrière?',
    'testimonials.banner.description': 'Rejoignez nos centaines de clients satisfaits et découvrez comment un CV professionnel peut changer votre vie professionnelle.',
    'testimonials.banner.cta': 'Commencer maintenant',
    
    // ATS Analysis Section
    'services.ats.title': 'Analyse ATS de votre CV',
    
    // Footer
    'footer.title': 'Besoin d\'aide? Parlons-en!',
    'footer.question': 'Vous avez une question sur mes services de rédaction de CV?',
    'footer.intro': 'Manon, mon assistante virtuelle, est là pour vous accompagner!',
    'footer.capabilities': 'Elle peut répondre à la plupart de vos questions et, au moment de commander votre CV, elle s\'assurera que vous n\'avez rien oublié. Selon votre profession, elle pourra même vous poser des questions ciblées pour mieux adapter votre CV à votre domaine.',
    'footer.appointment': 'Et si vous avez besoin d\'un échange plus personnalisé, Manon pourra aussi organiser un rendez-vous avec moi directement dans mon calendrier. 😊',
    'footer.form.name': 'Ton nom',
    'footer.form.phone': 'Si tu veux que je t\'appelle, inscris ton numéro de téléphone ici',
    'footer.form.email': 'Adresse courriel à laquelle je peux communiquer avec toi',
    'footer.form.message': 'Partages moi ta question ou donnes-moi quelques détails sur ton besoin',
    'footer.form.placeholder.message': 'Comment puis-je vous aider?',
    'footer.form.send': 'Envoyer',
    'footer.form.thankyou': 'J\'ai hâte d\'échanger avec vous et de vous aider à créer un CV qui mettra en valeur votre parcours et votre talent! 😊',
    'footer.copyright': '© {year} CV Pro Québec. Tous droits réservés.',
    'footer.privacy': 'Politique de confidentialité',
    'footer.terms': 'Conditions d\'utilisation',
  },
  en: {
    // Header
    'home': 'Home',
    'welcome': 'Welcome',
    'services': 'Services',
    'about': 'About',
    'templates': 'Templates',
    'testimonials': 'Testimonials',
    'blog': 'Blog',
    'contact': 'Contact',
    'start': 'Get Started',
    
    // Hero
    'expert.title': 'Professional Resume Writing Expert',
    'hero.title': 'Transform Your Career With a Winning Resume',
    'hero.description': 'Don\'t let your resume keep you from your dream job. In a competitive job market, every detail matters. With the rise of applications and automated processes (ATS), a poorly optimized resume may go unnoticed. Maximize your chances!',
    'hero.cta': 'Start my resume',
    'hero.secondary': 'Learn more',
    'hero.clients': 'Over 800 satisfied clients',
    
    // Welcome
    'welcome.title': 'WELCOME',
    'welcome.subtitle': 'Don\'t let your resume prevent you from getting your dream job.',
    'welcome.paragraph1': 'In a competitive job market, every detail matters. With the rise of applications and automated processes (ATS), a poorly optimized resume may go unnoticed. Maximize your chances!',
    'welcome.paragraph2': 'Entrust the writing and optimization of your resume to CV Pro Quebec and get more interviews with a professional, impactful document compatible with recruiters and automated selection systems (ATS).',
    'welcome.services': 'Our services',
    'welcome.start': 'Start now',
    
    // Services
    'services.title': 'Our professional writing services',
    'services.subtitle': 'Why choose CV Pro Quebec?',
    'services.intro': 'In an ultra-competitive job market, your resume is much more than a simple document — it\'s your entry ticket to land your dream interview. Unfortunately, even the most beautiful resume can close doors if it\'s not strategically optimized.',
    'services.traps.title': 'The pitfalls of standard solutions',
    'services.traps.description': 'Many sites offer attractive templates or self-writing tools, but these solutions have critical shortcomings:',
    'services.traps.item1': 'Incompatibility with ATS systems — 75% of resumes are automatically rejected before being read by a recruiter',
    'services.traps.item2': 'Generic content not adapted to the targeted position — absence of strategic keywords sought by recruiters',
    'services.traps.item3': 'Relevant skills omitted — your past experiences contain assets that you haven\'t highlighted',
    'services.traps.item4': 'Language problems — spelling, grammar, or syntax errors that harm your professionalism',
    'services.traps.item5': 'Lack of narrative coherence — absence of a thread that values your unique journey',
    
    // New Services Content in English
    'services.approach.title': 'Our approach: Strategic personalization',
    'services.approach.intro': 'At CV Pro Quebec, we don\'t just offer you a simple document, but a true professional positioning strategy:',
    
    'services.analysis.title': '1. In-depth analysis of your profile',
    'services.analysis.intro': 'Our process begins with comprehensive information gathering:',
    'services.analysis.item1': 'LinkedIn profile: your professional experience and training',
    'services.analysis.item2': 'Analysis of your previous resumes to identify untapped skills',
    'services.analysis.item3': 'Personalized questionnaire to discover your forgotten achievements',
    'services.analysis.item4': 'Guided conversation to understand your professional vision',
    
    'services.optimization.title': '2. Targeted optimization for the desired position',
    'services.optimization.intro': 'We create a tailored resume that:',
    'services.optimization.item1': 'Strategically incorporates keywords from the target job description',
    'services.optimization.item2': 'Highlights only relevant experiences and skills',
    'services.optimization.item3': 'Quantifies your achievements with concrete data',
    'services.optimization.item4': 'Scrupulously respects the standards of the Quebec Office of the French Language',
    'services.optimization.item5': 'Passes ATS filters with an optimal score',
    
    'services.support.title': '3. Comprehensive support to maximize your chances',
    'services.support.intro': 'Beyond the resume, we offer you:',
    'services.support.item1': 'Writing a powerful cover letter that strengthens your application',
    'services.support.item2': 'Practical advice to align your LinkedIn profile with your new resume',
    'services.support.item3': 'Personalized recommendations to prepare for likely interview questions',
    'services.support.item4': 'Post-delivery follow-up to answer your questions',
    
    'services.guarantee.title': 'Our guarantee of excellence',
    'services.guarantee.intro': 'Unlike generic services, we are committed to creating a document that:',
    'services.guarantee.item1': 'Faithfully represents your professional journey',
    'services.guarantee.item2': 'Highlights your most relevant skills for the position sought',
    'services.guarantee.item3': 'Respects the linguistic and presentation standards of the Quebec market',
    'services.guarantee.item4': 'Is optimized to successfully pass through ATS filters',
    'services.guarantee.item5': 'Positions you as the ideal candidate in the eyes of recruiters',
    
    'services.howto.title': 'How it works',
    'services.howto.step1': 'Step 1: Choose your package and place your order.',
    'services.howto.step2': 'Step 2: Submit your information (current resume, LinkedIn, job description, etc.).',
    'services.howto.step3': 'Step 3: Receive your personalized resume within 24 to 48 hours!',
    'services.howto.note': 'Why? It clarifies the process without going into technical details, and it reassures about ease of use.',
    
    'services.conclusion': 'A poorly optimized resume can cost you months of job searching and thousands of dollars in missed opportunities. Don\'t let a mediocre document stand between you and your dream job.',
    'services.tagline': 'CV Pro Quebec — Because your career deserves a document that matches your talent.',

    // About
    'about.title': 'Hello!',
    'about.subtitle': 'I am Gérald Doré, the heart behind CV Pro Québec.',
    'about.section1.title': 'My journey',
    'about.section1.description': 'Over the years, I have seen too many talents get lost in the shadow of a poorly presented resume. Brilliant engineers, passionate craftsmen, promising students – all qualified, but held back by details: spelling mistakes, poorly chosen action verbs, or chaotic layout.',

    // Testimonials
    'testimonials.title': 'What our clients say',
    'testimonials.subtitle': 'Discover how our services have helped professionals advance their careers',
    'testimonials.banner.title': 'Ready to transform your career?',
    'testimonials.banner.description': 'Join our hundreds of satisfied clients and discover how a professional resume can change your professional life.',
    'testimonials.banner.cta': 'Get started now',
    
    // ATS Analysis Section
    'services.ats.title': 'ATS Resume Analysis',
    
    // Footer
    'footer.title': 'Need help? Let\'s talk!',
    'footer.question': 'Do you have questions about my resume writing services?',
    'footer.intro': 'Manon, my virtual assistant, is here to support you!',
    'footer.capabilities': 'She can answer most of your questions and, when ordering your resume, she will make sure you haven\'t forgotten anything. Depending on your profession, she can even ask targeted questions to better adapt your resume to your field.',
    'footer.appointment': 'And if you need a more personalized exchange, Manon can also schedule an appointment with me directly in my calendar. 😊',
    'footer.form.name': 'Your name',
    'footer.form.phone': 'If you want me to call you, enter your phone number here',
    'footer.form.email': 'Email address where I can contact you',
    'footer.form.message': 'Share your question or give me some details about your needs',
    'footer.form.placeholder.message': 'How can I help you?',
    'footer.form.send': 'Send',
    'footer.form.thankyou': 'I look forward to connecting with you and helping you create a resume that will showcase your background and talent! 😊',
    'footer.copyright': '© {year} CV Pro Quebec. All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Use',
  },
};

const LanguageContext = createContext<LanguageContextType>({
  language: 'fr',
  setLanguage: () => {},
  t: () => '',
});

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('language');
    return (savedLanguage as Language) || 'fr';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations.fr] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
